import React, { useState, useEffect } from 'react';
import { 
  Calendar, 
  Users, 
  GraduationCap,
  CreditCard,
  CheckCircle,
  XCircle,
  Filter,
  BarChart3,
  User,
  BookOpen,
  TrendingUp,
  DollarSign,
  Clock,
  AlertCircle,
  Search
} from 'lucide-react';
import api from '../api';
import { toast } from 'react-toastify';

const ParentHistoriqueTab = ({ parent, parentId }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [selectedYear, setSelectedYear] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchHistoriqueData();
  }, [parentId, selectedYear]);

  const fetchHistoriqueData = async () => {
    setLoading(true);
    try {
      const params = selectedYear ? `?annee=${selectedYear}` : '';
      const response = await api.get(`/etudiant/parent-historique/${parentId}/${params}`);
      setData(response.data);
    } catch (error) {
      console.error('Erreur lors du chargement de l\'historique:', error);
      toast.error('Erreur lors du chargement de l\'historique');
    } finally {
      setLoading(false);
    }
  };

  const handleYearChange = (e) => {
    setSelectedYear(e.target.value);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  // Filtrer les données par terme de recherche
  const filteredHistorique = data?.historique?.filter(item => {
    if (!searchTerm) return true;
    
    const searchTermLower = searchTerm.toLowerCase();
    const etudiant = item.etudiant;
    
    return (
      etudiant.nom.toLowerCase().includes(searchTermLower) ||
      etudiant.prenom.toLowerCase().includes(searchTermLower) ||
      (etudiant.matricule && etudiant.matricule.toLowerCase().includes(searchTermLower)) ||
      `${etudiant.prenom} ${etudiant.nom}`.toLowerCase().includes(searchTermLower)
    );
  }) || [];

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'XOF'
    }).format(amount || 0);
  };

  const getStatusBadge = (status, isAnnuler) => {
    if (isAnnuler) {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
          <XCircle className="h-3 w-3 mr-1" />
          Annulé
        </span>
      );
    }
    
    return status ? (
      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
        <CheckCircle className="h-3 w-3 mr-1" />
        Actif
      </span>
    ) : (
      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
        <AlertCircle className="h-3 w-3 mr-1" />
        Inactif
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#202654]"></div>
        <span className="ml-3 text-gray-600">Chargement de l'historique...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* En-tête avec titre et description */}
      <div className="bg-gradient-to-r from-gray-50 to-blue-50 rounded-lg p-6">
        <div className="mb-4">
          <h2 className="text-2xl font-bold text-gray-900">Historique des Étudiants</h2>
          <p className="text-gray-600">Suivi des inscriptions et paiements</p>
        </div>

        {/* Statistiques rapides */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Users className="h-5 w-5 text-blue-600" />
              </div>
              <div className="ml-3">
                <p className="text-sm text-gray-600">Étudiants</p>
                <p className="text-xl font-semibold text-gray-900">{data?.statistiques?.total_etudiants || 0}</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg">
                <GraduationCap className="h-5 w-5 text-green-600" />
              </div>
              <div className="ml-3">
                <p className="text-sm text-gray-600">Inscriptions</p>
                <p className="text-xl font-semibold text-gray-900">{data?.statistiques?.total_inscriptions || 0}</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <CheckCircle className="h-5 w-5 text-emerald-600" />
              </div>
              <div className="ml-3">
                <p className="text-sm text-gray-600">Montant Payé</p>
                <p className="text-xl font-semibold text-gray-900">
                  {formatCurrency(data?.statistiques?.total_montant_paye)}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center">
              <div className="p-2 bg-red-100 rounded-lg">
                <XCircle className="h-5 w-5 text-red-600" />
              </div>
              <div className="ml-3">
                <p className="text-sm text-gray-600">Montant Impayé</p>
                <p className="text-xl font-semibold text-gray-900">
                  {formatCurrency(data?.statistiques?.total_montant_impaye)}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Liste des inscriptions */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="flex items-center">
              <BarChart3 className="h-5 w-5 mr-2" />
              <h3 className="text-lg font-semibold text-gray-900">
                Historique Détaillé
                {searchTerm && (
                  <span className="ml-2 text-sm font-normal text-gray-500">
                    ({filteredHistorique.length} sur {data?.historique?.length || 0})
                  </span>
                )}
              </h3>
            </div>
            
            {/* Filtres repositionnés */}
            <div className="flex flex-col sm:flex-row gap-3">
              {/* Barre de recherche */}
              <div className="relative">
                <input
                  type="text"
                  placeholder="Rechercher un étudiant..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#202654] focus:border-transparent bg-white min-w-[250px]"
                />
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-5 w-5 text-gray-400" />
                </div>
              </div>
              
              {/* Filtre par année */}
              <div className="flex items-center space-x-3">
                <Filter className="h-5 w-5 text-gray-400" />
                <select
                  value={selectedYear}
                  onChange={handleYearChange}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#202654] focus:border-transparent bg-white"
                >
                  <option value="">Toutes les années</option>
                  {data?.annees_academiques?.map(annee => (
                    <option key={annee.id} value={annee.id}>
                      {annee.libelle}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Indicateur de recherche */}
          {searchTerm && (
            <div className="mt-4 p-3 bg-blue-100 border border-blue-200 rounded-lg">
              <div className="flex items-center">
                <Search className="h-4 w-4 text-blue-600 mr-2" />
                <span className="text-sm text-blue-800">
                  Recherche pour "{searchTerm}" - {filteredHistorique.length} résultat{filteredHistorique.length > 1 ? 's' : ''} trouvé{filteredHistorique.length > 1 ? 's' : ''}
                </span>
                {filteredHistorique.length > 0 && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="ml-auto text-blue-600 hover:text-blue-800 text-sm underline"
                  >
                    Effacer
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
        
        <div className="p-6">
          {filteredHistorique.length > 0 ? (
            <div className="space-y-4">
              {filteredHistorique.map((item, index) => (
                <div
                  key={`${item.etudiant.id}-${item.inscription.id}`}
                  className="bg-gray-50 rounded-lg p-4 border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-all duration-200"
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center">
                      <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center mr-3">
                        <User className="h-5 w-5 text-white" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900">
                          {/* Surligner le terme de recherche */}
                          {searchTerm ? (
                            <span
                              dangerouslySetInnerHTML={{
                                __html: `${item.etudiant.prenom} ${item.etudiant.nom}`.replace(
                                  new RegExp(`(${searchTerm})`, 'gi'),
                                  '<mark class="bg-yellow-200 px-1 rounded">$1</mark>'
                                )
                              }}
                            />
                          ) : (
                            `${item.etudiant.prenom} ${item.etudiant.nom}`
                          )}
                        </h4>
                        <p className="text-sm text-gray-600">
                          Matricule: {searchTerm && item.etudiant.matricule ? (
                            <span
                              dangerouslySetInnerHTML={{
                                __html: (item.etudiant.matricule || 'Non assigné').replace(
                                  new RegExp(`(${searchTerm})`, 'gi'),
                                  '<mark class="bg-yellow-200 px-1 rounded">$1</mark>'
                                )
                              }}
                            />
                          ) : (
                            item.etudiant.matricule || 'Non assigné'
                          )}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      {getStatusBadge(item.inscription.status, item.inscription.is_annuler)}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="bg-white p-3 rounded border border-gray-200">
                      <div className="flex items-center mb-2">
                        <Calendar className="h-4 w-4 text-gray-500 mr-2" />
                        <span className="text-sm font-medium text-gray-700">Année</span>
                      </div>
                      <p className="text-sm text-gray-900">{item.inscription.annee.libelle}</p>
                    </div>

                    <div className="bg-white p-3 rounded border border-gray-200">
                      <div className="flex items-center mb-2">
                        <BookOpen className="h-4 w-4 text-gray-500 mr-2" />
                        <span className="text-sm font-medium text-gray-700">Formation</span>
                      </div>
                      <p className="text-sm text-gray-900">
                        {item.inscription.filiere?.libelle || 'Non spécifié'}
                      </p>
                      <p className="text-xs text-gray-500">
                        {item.inscription.niveau?.libelle || 'Non spécifié'}
                      </p>
                    </div>

                    <div className="bg-white p-3 rounded border border-gray-200">
                      <div className="flex items-center mb-2">
                        <GraduationCap className="h-4 w-4 text-gray-500 mr-2" />
                        <span className="text-sm font-medium text-gray-700">Classe</span>
                      </div>
                      <p className="text-sm text-gray-900">
                        {item.inscription.classe?.libelle || 'Non assigné'}
                      </p>
                    </div>

                    <div className="bg-white p-3 rounded border border-gray-200">
                      <div className="flex items-center mb-2">
                        <Clock className="h-4 w-4 text-gray-500 mr-2" />
                        <span className="text-sm font-medium text-gray-700">Inscription</span>
                      </div>
                      <p className="text-sm text-gray-900">
                        {formatDate(item.inscription.date_inscription)}
                      </p>
                    </div>
                  </div>

                  {/* Informations financières */}
                  <div className="mt-4 p-3 bg-white rounded border border-gray-200">
                    <div className="flex items-center mb-2">
                      <DollarSign className="h-4 w-4 text-gray-500 mr-2" />
                      <span className="text-sm font-medium text-gray-700">Situation Financière</span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="text-center">
                        <p className="text-lg font-semibold text-gray-900">
                          {formatCurrency(item.inscription.montant_total)}
                        </p>
                        <p className="text-xs text-gray-500">Montant Total</p>
                      </div>
                      <div className="text-center">
                        <p className="text-lg font-semibold text-green-600">
                          {formatCurrency(item.inscription.montant_paye)}
                        </p>
                        <p className="text-xs text-gray-500">Montant Payé</p>
                      </div>
                      <div className="text-center">
                        <p className="text-lg font-semibold text-red-600">
                          {formatCurrency(item.inscription.montant_impaye)}
                        </p>
                        <p className="text-xs text-gray-500">Montant Impayé</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <Users className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {searchTerm ? 'Aucun étudiant trouvé' : 'Aucun historique trouvé'}
              </h3>
              <p className="text-gray-500">
                {searchTerm 
                  ? `Aucun étudiant ne correspond à "${searchTerm}". Essayez avec un autre terme de recherche.`
                  : selectedYear 
                    ? 'Aucune inscription trouvée pour l\'année sélectionnée.' 
                    : 'Aucune inscription enregistrée pour ce parent.'}
              </p>
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="mt-4 text-blue-600 hover:text-blue-800 underline"
                >
                  Effacer la recherche
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ParentHistoriqueTab; 